import React from 'react'
import { cn } from '@/utils'

interface BadgeProps {
  variant?: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'destructive' | 'info'
  size?: 'xs' | 'sm' | 'md' | 'lg'
  className?: string
  children: React.ReactNode
}

const badgeVariants = {
  default: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
  primary: 'bg-jewelry-primary/10 text-jewelry-primary dark:bg-jewelry-primary/20',
  secondary: 'bg-jewelry-secondary/10 text-jewelry-secondary dark:bg-jewelry-secondary/20',
  success: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
  warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
  danger: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
  destructive: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
  info: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
}

const badgeSizes = {
  xs: 'px-1.5 py-0.5 text-xs',
  sm: 'px-2 py-1 text-xs',
  md: 'px-2.5 py-1 text-sm',
  lg: 'px-3 py-1.5 text-sm'
}

export const Badge: React.FC<BadgeProps> = ({
  variant = 'default',
  size = 'sm',
  className,
  children
}) => {
  return (
    <span
      className={cn(
        'inline-flex items-center font-medium rounded-full',
        badgeVariants[variant],
        badgeSizes[size],
        className
      )}
    >
      {children}
    </span>
  )
}

// Dot badge for status indicators
interface DotBadgeProps {
  variant?: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'info'
  size?: 'xs' | 'sm' | 'md' | 'lg'
  className?: string
  children?: React.ReactNode
}

const dotSizes = {
  xs: 'w-1.5 h-1.5',
  sm: 'w-2 h-2',
  md: 'w-2.5 h-2.5',
  lg: 'w-3 h-3'
}

const dotColors = {
  default: 'bg-gray-400 dark:bg-gray-500',
  primary: 'bg-jewelry-primary',
  secondary: 'bg-jewelry-secondary',
  success: 'bg-green-500',
  warning: 'bg-yellow-500',
  danger: 'bg-red-500',
  info: 'bg-blue-500'
}

export const DotBadge: React.FC<DotBadgeProps> = ({
  variant = 'default',
  size = 'sm',
  className,
  children
}) => {
  if (children) {
    return (
      <span className={cn('inline-flex items-center gap-2', className)}>
        <span
          className={cn(
            'rounded-full',
            dotColors[variant],
            dotSizes[size]
          )}
        />
        {children}
      </span>
    )
  }

  return (
    <span
      className={cn(
        'inline-block rounded-full',
        dotColors[variant],
        dotSizes[size],
        className
      )}
    />
  )
}

// Notification badge for counts
interface NotificationBadgeProps {
  count: number
  max?: number
  showZero?: boolean
  className?: string
}

export const NotificationBadge: React.FC<NotificationBadgeProps> = ({
  count,
  max = 99,
  showZero = false,
  className
}) => {
  if (count === 0 && !showZero) {
    return null
  }

  const displayCount = count > max ? `${max}+` : count.toString()

  return (
    <Badge
      variant="danger"
      size="xs"
      className={cn(
        'min-w-[1.25rem] h-5 flex items-center justify-center',
        className
      )}
    >
      {displayCount}
    </Badge>
  )
}